import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useConversionTracking } from "@/hooks/useConversionTracking";
import { trackEvent, trackOnboardingStep } from "@/hooks/useAnalyticsTracking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  Bot, Globe, Loader2, ArrowRight, CheckCircle2, AlertCircle,
  Search, Sparkles, Users, Target, Mail, Zap, Send, ChevronLeft,
  Wifi, Shield, Eye, Lock, Home, X, Pencil
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { OnboardingStepHeader } from "@/components/onboarding/OnboardingStepHeader";
import { verticalIcpDefaults, type VerticalIcpDefaults } from "@/lib/verticals";
import { useVerticalSeeds } from "@/hooks/useVerticalSeeds";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const fillFromDefaults = (
  current: string[] | undefined | null,
  fallback: string[] | undefined,
): string[] => {
  const arr = Array.isArray(current) ? current.filter(Boolean) : [];
  if (arr.length > 0) return arr;
  return fallback && fallback.length > 0 ? [...fallback] : [];
};

// ─── Types ───
type AnalysisResult = {
  company: { name: string; tagline: string; description: string; industry: string; value_proposition: string };
  brand: { tone: string; voice_notes: string; primary_color: string; secondary_color: string; logo_url: string };
  icp_signals: { target_industries: string[]; target_company_sizes: string[]; target_titles: string[]; target_regions: string[]; pain_points: string[]; use_cases: string[] };
  competitive_intel: { positioning: string; differentiators: string[]; tech_stack_signals: string[]; pricing_signals: string };
  messaging_angles: { angle: string; hook: string; value_prop: string }[];
  hiring_signals: { actively_hiring: boolean; growth_areas: string[]; team_size_estimate: string };
};

type OnboardingPhase = "input" | "building" | "confirm" | "activation" | "save_account" | "connect_email" | "connect_calendar";

// ─── Build Steps ───
const BUILD_STEPS = [
  { id: "scan", label: "Scanning your website", icon: Globe, duration: 3000 },
  { id: "brand", label: "Extracting brand identity", icon: Sparkles, duration: 1500 },
  { id: "icp", label: "Generating ideal customer profile", icon: Target, duration: 2000 },
  { id: "contacts", label: "Discovering target contacts", icon: Users, duration: 2500 },
  { id: "campaign", label: "Building your first campaign", icon: Send, duration: 2000 },
  { id: "emails", label: "Writing personalized emails", icon: Mail, duration: 1500 },
];

// ─── Google Icon ───
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// ─── Phase 1: Single Input ───
function InputPhase({ onSubmit, loading }: { onSubmit: (domain: string) => void; loading: boolean }) {
  const [domain, setDomain] = useState("");

  return (
    <div className="onboarding-shell min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md space-y-8"
      >
        <OnboardingStepHeader
          icon={Bot}
          variant="solid"
          glow
          title="Enter your website"
          subtitle="We'll build your entire sales pipeline in under 60 seconds."
        />

        <div className="space-y-3">
          <div className="relative">
            <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && domain.trim() && !loading && onSubmit(domain.trim())}
              placeholder="yourcompany.com"
              className="pl-10 h-12 text-base bg-muted/30 border-border"
              disabled={loading}
              autoFocus
            />
          </div>
          <Button
            size="lg"
            className="w-full h-12 text-sm gap-2 glow-primary"
            onClick={() => onSubmit(domain.trim())}
            disabled={!domain.trim() || loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            Build My Pipeline
          </Button>
        </div>

        <div className="flex items-center justify-center gap-4 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> No credit card</span>
          <span className="flex items-center gap-1"><Zap className="h-3 w-3" /> 60-second setup</span>
          <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> SOC 2</span>
        </div>

        <div className="text-center">
          <button
            onClick={async () => { await supabase.auth.signOut(); window.location.href = "/"; }}
            className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Sign out
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Phase 2: Auto-Build (Animated Progress) ───
function BuildingPhase({ currentStep, progress }: { currentStep: number; progress: number }) {
  return (
    <div className="onboarding-shell min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md space-y-8"
      >
        <OnboardingStepHeader
          icon={Sparkles}
          variant="solid"
          glow
          spin
          title="Your AI SDR is preparing your first meeting"
          subtitle="Setting up outreach on your behalf. You'll be notified when the first meeting is booked."
        />

        <Progress value={progress} className="h-2" />

        <div className="space-y-2">
          {BUILD_STEPS.map((step, i) => {
            const StepIcon = step.icon;
            const isDone = i < currentStep;
            const isActive = i === currentStep;

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: i <= currentStep ? 1 : 0.3, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                  isActive ? "bg-primary/5 border border-primary/20" : ""
                }`}
              >
                <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${
                  isDone ? "bg-primary/20" : isActive ? "bg-primary/10" : "bg-muted/30"
                }`}>
                  {isDone ? (
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  ) : isActive ? (
                    <Loader2 className="h-4 w-4 text-primary animate-spin" />
                  ) : (
                    <StepIcon className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <span className={`text-sm ${isDone ? "text-foreground" : isActive ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                  {step.label}
                </span>
                {isDone && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="ml-auto text-[10px] text-primary font-mono"
                  >
                    Done
                  </motion.span>
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

// ─── Phase 3: Activation Screen ───
function ActivationPhase({
  analysis,
  contactsFound,
  emailPreviews,
  onContinue,
}: {
  analysis: AnalysisResult;
  contactsFound: number;
  emailPreviews: { to: string; subject: string; preview: string }[];
  onContinue: () => void;
}) {
  return (
    <div className="onboarding-shell min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg space-y-6"
      >
        {/* Success header — outcome moment, brand-green glow */}
        <OnboardingStepHeader
          icon={CheckCircle2}
          glow
          title="Your AI SDR is ready"
          subtitle="From here, we'll work in the background to land your first qualified meeting."
        />

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-3 gap-3"
        >
          {[
            { value: contactsFound, label: "Contacts Found", icon: Users },
            { value: "1", label: "Campaign Created", icon: Target },
            { value: emailPreviews.length || "4", label: "Emails Written", icon: Mail },
          ].map((stat, i) => (
            <div key={stat.label} className="rounded-lg border border-border bg-card p-3 text-center">
              <stat.icon className="h-4 w-4 text-primary mx-auto mb-1" />
              <p className="text-lg font-bold font-mono text-foreground">{stat.value}</p>
              <p className="text-[10px] text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Company match */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-lg border border-border bg-card p-4 space-y-2"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-[10px] text-primary font-mono uppercase tracking-wider">AI-DETECTED BRAND</span>
          </div>
          <div className="flex items-start gap-3">
            {analysis.brand?.primary_color && (
              <div className="h-10 w-10 rounded-lg border border-border shrink-0" style={{ backgroundColor: analysis.brand.primary_color }} />
            )}
            <div>
              <p className="text-sm font-semibold text-foreground">{analysis.company?.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{analysis.company?.value_proposition}</p>
            </div>
          </div>
          {analysis.icp_signals?.target_titles?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {analysis.icp_signals.target_titles.slice(0, 4).map((t) => (
                <span key={t} className="text-[10px] px-2 py-0.5 rounded-md bg-primary/10 text-primary">{t}</span>
              ))}
            </div>
          )}
        </motion.div>

        {/* Email previews */}
        {emailPreviews.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-2"
          >
            <div className="flex items-center gap-2">
              <Eye className="h-3.5 w-3.5 text-primary" />
              <span className="text-[10px] text-primary font-mono uppercase tracking-wider">EMAIL PREVIEW</span>
            </div>
            <div className="rounded-lg border border-border bg-card divide-y divide-border">
              {emailPreviews.slice(0, 2).map((email, i) => (
                <div key={i} className="p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground">To:</span>
                    <span className="text-xs text-foreground">{email.to}</span>
                  </div>
                  <p className="text-xs font-medium text-foreground">{email.subject}</p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">{email.preview}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Messaging angles */}
        {analysis.messaging_angles?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="rounded-lg border border-border bg-muted/20 p-3 space-y-2"
          >
            <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">AI MESSAGING STRATEGY</span>
            {analysis.messaging_angles.slice(0, 2).map((m, i) => (
              <div key={i}>
                <p className="text-xs font-medium text-foreground">{m.angle}</p>
                <p className="text-[10px] text-primary italic">"{m.hook}"</p>
              </div>
            ))}
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Button size="lg" className="w-full h-12 text-sm gap-2 glow-primary" onClick={onContinue}>
            <Mail className="h-4 w-4" /> Connect Inbox to Start Booking Meetings
          </Button>
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            One last step — link the inbox your AI SDR will send from. You'll be notified when a meeting is booked. Your first qualified meeting is free.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

// ─── Phase 4: Email Connection (Conversion Event) ───
function ConnectEmailPhase({
  onConnected,
  onSkip,
  loading: parentLoading,
}: {
  onConnected: (email: string, method: "gmail" | "oauth", password?: string, provider?: string, name?: string) => void;
  onSkip: () => void;
  loading: boolean;
}) {
  const [showGmailForm, setShowGmailForm] = useState(false);
  const [gmailEmail, setGmailEmail] = useState("");
  const [gmailAppPassword, setGmailAppPassword] = useState("");
  const [oauthLoading, setOauthLoading] = useState<"google" | "microsoft" | null>(null);
  const [oauthError, setOauthError] = useState("");
  const [gmailConnecting, setGmailConnecting] = useState(false);
  const { toast } = useToast();

  const handleGmailConnect = () => {
    if (!gmailEmail.trim() || !gmailAppPassword.trim()) return;
    setGmailConnecting(true);
    // Mark as pending — actual connection happens in handleComplete
    onConnected(gmailEmail.trim(), "gmail", gmailAppPassword);
    setGmailConnecting(false);
  };

  const handleOAuth = async (provider: "google" | "microsoft") => {
    setOauthLoading(provider);
    setOauthError("");
    try {
      const initRes = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/oauth-email-connect?action=init`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
          body: JSON.stringify({ provider }),
        }
      );
      const initResult = await initRes.json();
      if (!initResult.success || !initResult.auth_url) throw new Error(initResult.error || "Failed to start OAuth");

      const sessionId = initResult.session_id;
      const popup = window.open(initResult.auth_url, `${provider}-oauth`, "width=500,height=650,scrollbars=yes,left=200,top=100");

      for (let i = 0; i < 150; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        if (popup && popup.closed && i > 2) break;
        const statusRes = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/oauth-email-connect?action=status&session_id=${sessionId}`,
          { headers: { "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } }
        );
        const statusResult = await statusRes.json();
        if (statusResult.status === "success") {
          popup?.close();
          onConnected(statusResult.email, "oauth", undefined, provider, statusResult.name);
          return;
        }
        if (statusResult.status === "error") throw new Error(statusResult.error_description || "OAuth failed");
        if (statusResult.status === "expired") throw new Error("Session expired. Please try again.");
      }
      throw new Error("Connection timed out. Please try again.");
    } catch (err: any) {
      setOauthError(err.message || "Something went wrong");
      toast({ title: "Connection failed", description: err.message, variant: "destructive" });
    } finally {
      setOauthLoading(null);
    }
  };

  return (
    <div className="onboarding-shell min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-6"
      >
        <OnboardingStepHeader
          icon={Send}
          title="Connect your sending account"
          subtitle="This is the inbox your AI SDR will send from. We start with a low daily volume and ramp up as your sender reputation builds — usually 1–2 weeks."
        />

        <AnimatePresence mode="wait">
          {showGmailForm ? (
            <motion.div key="gmail" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
              <button type="button" onClick={() => setShowGmailForm(false)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                <ChevronLeft className="h-3 w-3" /> Back
              </button>
              <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <GoogleIcon />
                  <p className="text-sm font-medium text-foreground">Connect Google Account</p>
                </div>
                <div className="rounded-md border border-primary/20 bg-primary/5 p-2.5 space-y-1">
                  <p className="text-[10px] text-foreground font-medium">How to get your App Password:</p>
                  <ol className="text-[10px] text-muted-foreground space-y-0.5 list-decimal ml-3">
                    <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-primary underline">myaccount.google.com/apppasswords</a></li>
                    <li>2-Step Verification must be enabled first</li>
                    <li>Create an app password (select "Mail")</li>
                    <li>Copy the 16-character password below</li>
                  </ol>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Gmail Address</Label>
                  <Input value={gmailEmail} onChange={(e) => setGmailEmail(e.target.value)} placeholder="you@company.com" type="email" className="bg-muted/30 border-border text-xs" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">App Password</Label>
                  <Input value={gmailAppPassword} onChange={(e) => setGmailAppPassword(e.target.value)} placeholder="xxxx xxxx xxxx xxxx" type="password" className="bg-muted/30 border-border text-xs font-mono" />
                </div>
                <Button size="sm" className="w-full text-xs" onClick={handleGmailConnect} disabled={gmailConnecting || !gmailEmail.trim() || !gmailAppPassword.trim()}>
                  {gmailConnecting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Connect Gmail
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div key="options" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
              {/* Google */}
              <button
                type="button"
                onClick={() => setShowGmailForm(true)}
                disabled={!!oauthLoading}
                className="w-full flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                <div className="h-10 w-10 rounded-md bg-blue-500/10 flex items-center justify-center shrink-0">
                  <GoogleIcon />
                </div>
                <div className="text-left flex-1">
                  <p className="text-sm font-medium text-foreground">Google Account</p>
                  <p className="text-[10px] text-muted-foreground">Gmail & Google Workspace. Quick setup with App Password.</p>
                </div>
              </button>

              {/* Microsoft */}
              <button
                type="button"
                onClick={() => handleOAuth("microsoft")}
                disabled={!!oauthLoading}
                className="w-full flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                <div className="h-10 w-10 rounded-md bg-blue-600/10 flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 21 21" className="h-4 w-4">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                </div>
                <div className="text-left flex-1">
                  <p className="text-sm font-medium text-foreground">Microsoft 365</p>
                  <p className="text-[10px] text-muted-foreground">Outlook & Office 365. Secure OAuth sign-in.</p>
                </div>
                {oauthLoading === "microsoft" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
              </button>

              {oauthLoading && (
                <p className="text-[10px] text-muted-foreground text-center animate-pulse">
                  Waiting for sign-in in the popup window...
                </p>
              )}

              {oauthError && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5">
                  <p className="text-[10px] text-destructive flex items-center gap-1.5">
                    <AlertCircle className="h-3 w-3" /> {oauthError}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="text-center space-y-2">
          <button
            onClick={onSkip}
            disabled={parentLoading}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {parentLoading ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> : null}
            Skip for now — I'll connect later
          </button>
          <p className="text-[10px] text-muted-foreground">
            You can always connect your email from Settings.
          </p>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Phase 5: Calendar Connection (Skippable) ───
function ConnectCalendarPhase({
  onConnect,
  onSkip,
  loading,
  hasEmailConnected,
}: {
  onConnect: (provider: "google" | "microsoft") => void;
  onSkip: () => void;
  loading: boolean;
  hasEmailConnected: boolean;
}) {
  const [pending, setPending] = useState<"google" | "microsoft" | null>(null);

  const click = (provider: "google" | "microsoft") => {
    setPending(provider);
    onConnect(provider);
  };

  return (
    <div className="onboarding-shell min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-6"
      >
        <OnboardingStepHeader
          icon={CheckCircle2}
          title="Turn on automatic scheduling"
          subtitle="Optional, but it's the difference between manual confirmation and the AI handling the whole booking flow end-to-end."
        />

        <div className="rounded-lg border border-primary/20 bg-primary/[0.04] p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-primary">With calendar connected</span>
          </div>
          <ul className="text-[11px] text-foreground/90 space-y-1 list-disc ml-4">
            <li>Meeting requests are auto-confirmed with a real meeting link</li>
            <li>Conflicts are checked against your live availability</li>
            <li>24h + 1h reminders fire automatically for both sides</li>
          </ul>
          <div className="pt-1.5 border-t border-border/40 space-y-1 mt-1.5">
            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Without it (still works)</span>
            <ul className="text-[11px] text-muted-foreground space-y-1 list-disc ml-4">
              <li>Requests still land in your queue — you confirm and paste the link manually</li>
              <li>You can connect a calendar anytime from Settings → Booking</li>
            </ul>
          </div>
        </div>

        <div className="space-y-2">
          <button
            type="button"
            onClick={() => click("google")}
            disabled={!!pending || loading}
            className="w-full flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/50 transition-colors disabled:opacity-50"
          >
            <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <GoogleIcon />
            </div>
            <div className="text-left flex-1">
              <p className="text-sm font-medium text-foreground">Google Calendar</p>
              <p className="text-[10px] text-muted-foreground">Workspace & personal Gmail. Secure OAuth.</p>
            </div>
            {pending === "google" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          </button>
          <button
            type="button"
            onClick={() => click("microsoft")}
            disabled={!!pending || loading}
            className="w-full flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/50 transition-colors disabled:opacity-50"
          >
            <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <svg viewBox="0 0 21 21" className="h-4 w-4">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
            </div>
            <div className="text-left flex-1">
              <p className="text-sm font-medium text-foreground">Microsoft 365 / Outlook</p>
              <p className="text-[10px] text-muted-foreground">Office 365 calendars. Secure OAuth.</p>
            </div>
            {pending === "microsoft" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          </button>
        </div>

        <div className="text-center space-y-2">
          <button
            onClick={onSkip}
            disabled={loading || !!pending}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> : null}
            Skip for now — I'll confirm meetings manually
          </button>
          <p className="text-[10px] text-muted-foreground">
            You can connect a calendar anytime from Settings → Booking.
            {!hasEmailConnected && " Email isn't connected either — that's fine, you can add both later."}
          </p>
        </div>
      </motion.div>
    </div>
  );
}


// ─── Chip editor for ICP lists ───
function ChipEditor({
  label,
  values,
  onChange,
  placeholder,
  fieldName,
  onEditTracked,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  fieldName: string;
  onEditTracked?: (field: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const editedRef = useRef(false);
  const markEdited = () => {
    if (editedRef.current) return;
    editedRef.current = true;
    onEditTracked?.(fieldName);
  };
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...values, v]);
    setDraft("");
    markEdited();
  };
  const remove = (v: string) => {
    onChange(values.filter((x) => x !== v));
    markEdited();
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="rounded-md border border-border bg-muted/20 p-2 min-h-[42px] flex flex-wrap gap-1.5">
        {values.length === 0 && (
          <span className="text-[11px] text-muted-foreground px-1 py-0.5">Nothing detected — add one below.</span>
        )}
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 text-[11px] rounded-md bg-primary/10 text-primary px-2 py-0.5"
          >
            {v}
            <button
              type="button"
              onClick={() => remove(v)}
              className="hover:text-foreground"
              aria-label={`Remove ${v}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="bg-muted/30 border-border h-9 text-sm flex-1"
        />
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={add} disabled={!draft.trim()}>
          Add
        </Button>
      </div>
    </div>
  );
}

// ─── Best-effort vertical pre-selection from AI analysis ───
function guessVerticalSlug(
  seeds: { slug: string; label: string; description?: string }[],
  hints: string[],
): string | null {
  const normalized = hints
    .map((h) => (h || "").toLowerCase().trim())
    .filter(Boolean);
  if (normalized.length === 0) return null;
  for (const seed of seeds) {
    const hay = `${seed.slug} ${seed.label} ${seed.description ?? ""}`.toLowerCase();
    for (const hint of normalized) {
      if (!hint) continue;
      if (hay.includes(hint) || hint.includes(seed.slug.replace(/[-_]/g, " "))) {
        return seed.slug;
      }
    }
  }
  return null;
}

// ─── Phase 2.5: Confirm & edit AI-extracted setup ───
function ConfirmSetupPhase({
  analysis,
  domain,
  initialSenderName,
  initialSenderEmail,
  onSubmit,
  onBack,
}: {
  analysis: AnalysisResult;
  domain: string;
  initialSenderName: string;
  initialSenderEmail: string;
  onSubmit: (payload: {
    analysis: AnalysisResult;
    verticalSlug: string | null;
    senderName: string;
    senderEmail: string;
    editCounts: number;
  }) => void;
  onBack: () => void;
}) {
  const { verticals, isLoading: verticalsLoading } = useVerticalSeeds();

  // Local editable copies — seeded from analysis (with safe fallbacks).
  const [companyName, setCompanyName] = useState(analysis.company?.name || domain);
  const [valueProp, setValueProp] = useState(analysis.company?.value_proposition || "");
  const [titles, setTitles] = useState<string[]>(analysis.icp_signals?.target_titles ?? []);
  const [industries, setIndustries] = useState<string[]>(analysis.icp_signals?.target_industries ?? []);
  const [senderName, setSenderName] = useState(initialSenderName);
  const [senderEmail, setSenderEmail] = useState(initialSenderEmail);
  const [verticalSlug, setVerticalSlug] = useState<string>("");

  // Track which fields were pre-filled (for analytics + subtle "AI-filled" chip).
  const prefill = useRef({
    company: !!analysis.company?.name,
    valueProp: !!analysis.company?.value_proposition,
    titles: (analysis.icp_signals?.target_titles?.length ?? 0) > 0,
    industries: (analysis.icp_signals?.target_industries?.length ?? 0) > 0,
    senderName: !!initialSenderName,
    senderEmail: !!initialSenderEmail,
  });

  const editedFields = useRef<Set<string>>(new Set());
  const trackFieldEdit = (field: string) => {
    if (editedFields.current.has(field)) return;
    editedFields.current.add(field);
    trackEvent("onboarding_confirm_edited", { field });
  };

  // Best-effort vertical pre-selection once seeds arrive.
  useEffect(() => {
    if (verticalSlug || verticalsLoading || verticals.length === 0) return;
    const hints: string[] = [
      analysis.company?.industry ?? "",
      ...(analysis.icp_signals?.target_industries ?? []),
    ];
    const guess = guessVerticalSlug(verticals, hints);
    if (guess) setVerticalSlug(guess);
  }, [verticals, verticalsLoading, verticalSlug, analysis]);

  // Fire "shown" analytics once.
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    trackEvent("onboarding_confirm_shown", {
      prefilled: prefill.current,
      domain,
    });
  }, [domain]);

  const handleSubmit = () => {
    // Merge edits back into the analysis object.
    const nextAnalysis: AnalysisResult = {
      ...analysis,
      company: {
        ...analysis.company,
        name: companyName.trim() || domain,
        value_proposition: valueProp.trim(),
      },
      icp_signals: {
        ...analysis.icp_signals,
        target_titles: titles,
        target_industries: industries,
      },
    };
    trackEvent("onboarding_confirm_submitted", {
      edits: editedFields.current.size,
      edited_fields: Array.from(editedFields.current),
      vertical_slug: verticalSlug || null,
    });
    onSubmit({
      analysis: nextAnalysis,
      verticalSlug: verticalSlug || null,
      senderName: senderName.trim(),
      senderEmail: senderEmail.trim(),
      editCounts: editedFields.current.size,
    });
  };

  const canSubmit = companyName.trim().length > 0 && senderEmail.trim().length > 0;

  return (
    <div className="onboarding-shell min-h-screen flex items-center justify-center bg-background p-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-xl space-y-6"
      >
        <OnboardingStepHeader
          icon={Pencil}
          variant="solid"
          glow
          title="Confirm your setup"
          subtitle="We pulled this from your site — tweak anything that's off, then launch."
        />

        <div className="rounded-xl border border-border bg-card p-5 space-y-5">
          {/* Company name */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="cs-company" className="text-xs">Company name</Label>
              {prefill.current.company && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-primary/80">AI-filled</span>
              )}
            </div>
            <Input
              id="cs-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              onBlur={() => trackFieldEdit("company_name")}
              className="bg-muted/30 border-border h-10"
              placeholder="Your company"
            />
          </div>

          {/* Value proposition */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="cs-vp" className="text-xs">One-line value proposition</Label>
              {prefill.current.valueProp && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-primary/80">AI-filled</span>
              )}
            </div>
            <Textarea
              id="cs-vp"
              value={valueProp}
              onChange={(e) => setValueProp(e.target.value)}
              onBlur={() => trackFieldEdit("value_proposition")}
              rows={2}
              className="bg-muted/30 border-border resize-none text-sm"
              placeholder="e.g. We help mid-market SaaS companies cut churn 30% with AI-driven onboarding."
            />
          </div>

          {/* Vertical */}
          <div className="space-y-1.5">
            <Label className="text-xs">Target vertical</Label>
            <Select
              value={verticalSlug || undefined}
              onValueChange={(v) => {
                setVerticalSlug(v);
                trackFieldEdit("vertical");
              }}
            >
              <SelectTrigger className="bg-muted/30 border-border h-10 text-sm">
                <SelectValue placeholder={verticalsLoading ? "Loading verticals…" : "Select a vertical (optional)"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {verticals.map((v) => (
                  <SelectItem key={v.slug} value={v.slug}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">
              Sets sensible defaults for titles, industries, and pain points where the scan came up short.
            </p>
          </div>

          {/* ICP — titles */}
          <ChipEditor
            label="Target titles (ICP)"
            values={titles}
            onChange={setTitles}
            placeholder="e.g. VP of Sales"
            fieldName="target_titles"
            onEditTracked={trackFieldEdit}
          />

          {/* ICP — industries */}
          <ChipEditor
            label="Target industries"
            values={industries}
            onChange={setIndustries}
            placeholder="e.g. B2B SaaS"
            fieldName="target_industries"
            onEditTracked={trackFieldEdit}
          />

          {/* Primary contact */}
          <div className="pt-2 border-t border-border/60 space-y-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              Primary contact — the sender of your outbound emails
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cs-sender-name" className="text-xs">Name</Label>
                <Input
                  id="cs-sender-name"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  onBlur={() => trackFieldEdit("sender_name")}
                  className="bg-muted/30 border-border h-10"
                  placeholder="Alex Rivera"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cs-sender-email" className="text-xs">Email</Label>
                <Input
                  id="cs-sender-email"
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  onBlur={() => trackFieldEdit("sender_email")}
                  className="bg-muted/30 border-border h-10"
                  placeholder="alex@company.com"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="lg" className="h-12 gap-2" onClick={onBack}>
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
          <Button
            size="lg"
            className="flex-1 h-12 gap-2 glow-primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            <CheckCircle2 className="h-4 w-4" /> Looks good — activate
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Save Account phase (anonymous → permanent) ───
function SaveAccountPhase({
  initialName,
  initialEmail,
  onSaved,
}: {
  initialName: string;
  initialEmail: string;
  onSaved: (v: { email: string; fullName: string }) => void;
}) {
  const [fullName, setFullName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    trackEvent("onboarding_account_capture_shown");
    trackOnboardingStep("save_account", "started");
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const { error: updErr } = await supabase.auth.updateUser({
        email: email.trim(),
        password,
        data: { full_name: fullName.trim() || undefined },
      });
      if (updErr) throw updErr;
      trackEvent("onboarding_account_capture_submitted", { email });
      trackOnboardingStep("save_account", "completed");
      toast({ title: "Account saved", description: "Your workspace is now linked to your email." });
      onSaved({ email: email.trim(), fullName: fullName.trim() });
    } catch (err: any) {
      const msg = err?.message ?? "Please try again.";
      setError(msg);
      trackEvent("onboarding_account_capture_failed", { message: msg });
      trackOnboardingStep("save_account", "failed", { error: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="onboarding-shell min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <div className="max-w-md w-full rounded-2xl glass-surface p-7 space-y-6 relative z-10">
        <OnboardingStepHeader
          icon={Lock}
          title="Save your account"
          subtitle="Add an email and password so you can log back in and pick up where you left off."
        />
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Full name</Label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Work email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 6 characters"
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>
          {error && (
            <p className="text-xs text-destructive flex items-start gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {error}
            </p>
          )}
          <Button type="submit" size="lg" className="w-full h-11 gap-2" disabled={saving}>
            {saving ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
            ) : (
              <>Save and continue <ArrowRight className="h-4 w-4" /></>
            )}
          </Button>
          <p className="text-[10px] text-muted-foreground text-center">
            We'll link your workspace, prospects, and drafts to this account.
          </p>
        </form>
      </div>
    </div>
  );
}

const ONBOARDING_STORAGE_KEY = "autopitch_onboarding_state_v1";
const TRIAL_PREVIEW_STORAGE_KEY = "autopitch.trial_preview_v1";


type PersistedOnboardingState = {
  phase: OnboardingPhase;
  domain: string;
  analysis: AnalysisResult | null;
  contactsFound: number;
  emailPreviews: { to: string; subject: string; preview: string }[];
  connectedEmail: string;
};

type TrialPreviewSnapshot = {
  hostname?: string | null;
  savedAt?: number;
  preview?: {
    icp?: { title?: string; summary?: string };
    pains?: string[];
    angle?: string;
    opener?: string[];
    email?: {
      subject?: string;
      hook?: string;
      value?: string;
      cta?: string;
      personaRole?: string;
    };
    sender?: {
      company?: string;
      hostname?: string;
      logoUrl?: string;
      primaryColor?: string;
      secondaryColor?: string;
    };
    sequence?: Array<{ subject?: string; teaser?: string }>;
    onePagerContent?: {
      positioningLine?: string;
      outcomeHeadline?: string;
      outcomeSubhead?: string;
      whoItIsFor?: string;
    };
  };
};

function normalizeDomain(value: string = "") {
  try {
    const withProto = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    return new URL(withProto).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return value.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0].toLowerCase();
  }
}

function titleCaseHostname(domain: string) {
  const root = normalizeDomain(domain).split(".")[0]?.replace(/[-_]/g, " ").trim();
  return root ? root.replace(/\b\w/g, (char) => char.toUpperCase()) : "Company";
}

function loadTrialPreviewForDomain(inputDomain: string): TrialPreviewSnapshot | null {
  try {
    const raw =
      localStorage.getItem(TRIAL_PREVIEW_STORAGE_KEY) ??
      sessionStorage.getItem(TRIAL_PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TrialPreviewSnapshot;
    const storedHost = normalizeDomain(parsed.hostname || parsed.preview?.sender?.hostname || "");
    const requestedHost = normalizeDomain(inputDomain);
    const freshEnough = !parsed.savedAt || Date.now() - parsed.savedAt < 60 * 60 * 1000;
    if (!freshEnough) return null;
    if (storedHost && requestedHost && storedHost !== requestedHost) return null;
    return parsed?.preview ? parsed : null;
  } catch {
    return null;
  }
}

function analysisFromTrialPreview(snapshot: TrialPreviewSnapshot, inputDomain: string): AnalysisResult {
  const preview = snapshot.preview ?? {};
  const sender = preview.sender ?? {};
  const companyName = sender.company || titleCaseHostname(snapshot.hostname || inputDomain);
  const domain = normalizeDomain(sender.hostname || snapshot.hostname || inputDomain);
  const onePager = preview.onePagerContent ?? {};
  const email = preview.email ?? {};
  const valueProp =
    onePager.positioningLine ||
    onePager.outcomeHeadline ||
    email.value ||
    preview.angle ||
    `${companyName} helps its buyers solve a specific operational problem.`;
  const description = onePager.outcomeSubhead || preview.icp?.summary || valueProp;
  const personaRole = email.personaRole?.trim();
  const titleFromIcp = preview.icp?.title?.replace(/^(heads?|leaders?)\s+of\s+/i, "").trim();
  const targetTitles = [personaRole, titleFromIcp, "VP of Operations", "Head of Growth"]
    .filter((title): title is string => Boolean(title && title.length > 2))
    .filter((title, index, arr) => arr.findIndex((item) => item.toLowerCase() === title.toLowerCase()) === index)
    .slice(0, 6);
  const pains = Array.isArray(preview.pains) && preview.pains.length > 0
    ? preview.pains
    : ["Manual workflows slow down execution", "Leaders lack clear visibility into what drives outcomes"];
  const sequenceAngles = Array.isArray(preview.sequence) ? preview.sequence : [];

  return {
    company: {
      name: companyName,
      tagline: onePager.positioningLine || preview.angle || companyName,
      description,
      industry: "Software",
      value_proposition: valueProp,
    },
    brand: {
      tone: "professional",
      voice_notes: `${companyName} should sound direct, practical, and grounded in buyer outcomes.`,
      primary_color: sender.primaryColor || "#3b82f6",
      secondary_color: sender.secondaryColor || "#0f172a",
      logo_url: sender.logoUrl || "",
    },
    icp_signals: {
      target_industries: ["B2B companies"],
      target_company_sizes: ["mid-market", "enterprise"],
      target_titles: targetTitles.length > 0 ? targetTitles : ["VP of Operations", "Head of Growth", "Chief Revenue Officer"],
      target_regions: ["North America"],
      pain_points: pains.slice(0, 4),
      use_cases: [valueProp, preview.angle || "Improve conversion from target accounts"]
        .filter((item): item is string => Boolean(item))
        .slice(0, 4),
    },
    competitive_intel: {
      positioning: `${companyName} is positioned around ${valueProp.toLowerCase()}`,
      differentiators: [onePager.positioningLine, onePager.outcomeHeadline, preview.angle]
        .filter((item): item is string => Boolean(item && item.length > 3))
        .slice(0, 4),
      tech_stack_signals: domain ? [`Website: ${domain}`] : [],
      pricing_signals: "unknown",
    },
    messaging_angles: [
      {
        angle: preview.angle || "Relevant account trigger",
        hook: email.hook || preview.opener?.[0] || `Noticed a relevant shift for ${companyName}'s target buyers.`,
        value_prop: email.value || valueProp,
      },
      ...sequenceAngles.slice(0, 2).map((step) => ({
        angle: step.subject || "Follow-up angle",
        hook: step.teaser || step.subject || "A focused follow-up for the same buyer.",
        value_prop: valueProp,
      })),
    ],
    hiring_signals: {
      actively_hiring: false,
      growth_areas: ["Go-to-market execution"],
      team_size_estimate: "medium",
    },
  };
}

function loadPersistedOnboarding(): Partial<PersistedOnboardingState> | null {
  try {
    // Prefer durable localStorage; fall back to legacy sessionStorage entries.
    const raw =
      localStorage.getItem(ONBOARDING_STORAGE_KEY) ??
      sessionStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persistOnboarding(state: PersistedOnboardingState) {
  try {
    const serialized = JSON.stringify(state);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, serialized);
    sessionStorage.setItem(ONBOARDING_STORAGE_KEY, serialized);
  } catch {
    /* quota / private mode — ignore */
  }
}

function clearPersistedOnboarding() {
  try {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
    localStorage.removeItem("autopitch.trial_domain");
    sessionStorage.removeItem("autopitch.trial_domain");
    localStorage.removeItem(TRIAL_PREVIEW_STORAGE_KEY);
    sessionStorage.removeItem(TRIAL_PREVIEW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// ─── Main Onboarding Orchestrator ───
function OnboardingFlow() {
  const persisted = typeof window !== "undefined" ? loadPersistedOnboarding() : null;

  // If we were mid-build when unloaded, jump to activation if analysis exists, else restart from input
  const initialPhase: OnboardingPhase =
    persisted?.phase === "building"
      ? persisted.analysis
        ? "confirm"
        : "input"
      : (persisted?.phase as OnboardingPhase) || "input";

  const [phase, setPhase] = useState<OnboardingPhase>(initialPhase);
  const [buildStep, setBuildStep] = useState(0);
  const [buildProgress, setBuildProgress] = useState(0);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(persisted?.analysis ?? null);
  const [contactsFound, setContactsFound] = useState(persisted?.contactsFound ?? 0);
  const [emailPreviews, setEmailPreviews] = useState<{ to: string; subject: string; preview: string }[]>(
    persisted?.emailPreviews ?? []
  );
  const [domain, setDomain] = useState(persisted?.domain ?? "");
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const { toast } = useToast();
  const { track } = useConversionTracking();
  const buildIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Email connection state
  const [emailMethod, setEmailMethod] = useState<"gmail" | "oauth" | null>(null);
  const [connectedEmail, setConnectedEmail] = useState(persisted?.connectedEmail ?? "");
  const [gmailPassword, setGmailPassword] = useState("");
  const [oauthProvider, setOauthProvider] = useState("");
  const [oauthName, setOauthName] = useState("");

  // Confirmed setup (from ConfirmSetupPhase) — overrides analysis-derived defaults in finalize
  const [confirmedVerticalSlug, setConfirmedVerticalSlug] = useState<string | null>(null);
  const [confirmedSenderName, setConfirmedSenderName] = useState<string>("");
  const [confirmedSenderEmail, setConfirmedSenderEmail] = useState<string>("");
  const [authUserDefaults, setAuthUserDefaults] = useState<{ name: string; email: string }>({ name: "", email: "" });
  const [isAnonymousUser, setIsAnonymousUser] = useState(false);

  // Load auth-user defaults once for the Confirm step's Primary Contact fields
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled || !user) return;
      setAuthUserDefaults({
        name: (user.user_metadata?.full_name as string | undefined) ?? "",
        email: user.email ?? "",
      });
      setIsAnonymousUser(Boolean((user as any).is_anonymous));
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    track("onboarding_start");
    trackEvent("onboarding_started");
    trackOnboardingStep("input", "started");

    // If the user came from the landing URL trial, the domain was stashed in
    // sessionStorage. Skip the "enter your website" step and start building.
    if (phase === "input" && !domain) {
      try {
        const stashed =
          localStorage.getItem("autopitch.trial_domain") ??
          sessionStorage.getItem("autopitch.trial_domain");
        if (stashed) {
          // Keep the stash in place — clearPersistedOnboarding() removes it on
          // completion so a refresh mid-build still resumes automatically.
          // fire and forget — handleDomainSubmit manages its own state
          handleDomainSubmit(stashed);
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track current phase as a "started" event when it changes, and fire an
  // "abandoned" event for the active step if the user closes the tab mid-flow.
  const phaseRef = useRef(phase);
  const verticalPreviewRef = useRef<any[] | null>(null);
  useEffect(() => {
    if (phaseRef.current !== phase) {
      trackOnboardingStep(phase, "started");
      phaseRef.current = phase;
    }
  }, [phase]);

  useEffect(() => {
    const onUnload = () => {
      // Use sendBeacon-friendly path: trackEvent already fires fast async insert
      try {
        trackOnboardingStep(phaseRef.current, "abandoned", { domain });
      } catch {
        // ignore
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [domain]);

  // Persist key state on changes so tab switches / mobile remounts don't reset progress
  useEffect(() => {
    persistOnboarding({
      phase,
      domain,
      analysis,
      contactsFound,
      emailPreviews,
      connectedEmail,
    });
  }, [phase, domain, analysis, contactsFound, emailPreviews, connectedEmail]);

  // Animate build steps
  const startBuildAnimation = (realStepCount: number) => {
    let step = 0;
    const totalSteps = BUILD_STEPS.length;
    const tick = () => {
      step++;
      if (step <= totalSteps) {
        setBuildStep(step);
        setBuildProgress(Math.min(Math.round((step / totalSteps) * 100), 95));
      }
    };

    // Advance every ~2s but pace it so the real work finishes around the same time
    buildIntervalRef.current = setInterval(tick, 2200);
    return () => {
      if (buildIntervalRef.current) clearInterval(buildIntervalRef.current);
    };
  };

  const completeBuildFromAnalysis = (a: AnalysisResult, inputDomain: string) => {
    setAnalysis(a);

    // Kick off vertical auto-discovery NOW (in background) while user finishes onboarding.
    // No tenant_id yet; we cache the AI result and apply it instantly at finalize.
    verticalPreviewRef.current = null;
    const icpSig: any = a.icp_signals ?? {};
    supabase.functions
      .invoke("discover-verticals", {
        body: {
          preview: true,
          max: 10,
          icp: {
            company_name: a.company?.name ?? inputDomain,
            company_domain: inputDomain,
            value_proposition: a.company?.value_proposition ?? null,
            industries: icpSig.target_industries ?? [],
            titles: icpSig.target_titles ?? [],
            regions: icpSig.target_regions ?? [],
            company_sizes: icpSig.target_company_sizes ?? [],
            pain_points: icpSig.pain_points ?? [],
          },
        },
      })
      .then(({ data, error }) => {
        if (error) { console.warn("vertical preview failed", error); return; }
        if (Array.isArray((data as any)?.verticals)) {
          verticalPreviewRef.current = (data as any).verticals;
        }
      })
      .catch((e) => console.warn("vertical preview threw", e));

    // Generate mock email previews from messaging angles
    const titles = a.icp_signals?.target_titles || [];
    const previews = (a.messaging_angles || []).slice(0, 3).map((m, i) => ({
      to: `${titles[i] || "Decision Maker"} at Target Company`,
      subject: m.hook || m.angle,
      preview: m.value_prop || a.company?.value_proposition || "",
    }));
    setEmailPreviews(previews);
    setContactsFound(titles.length > 0 ? Math.max(titles.length * 3, 8) : 12);

    if (buildIntervalRef.current) clearInterval(buildIntervalRef.current);
    setBuildStep(BUILD_STEPS.length);
    setBuildProgress(100);

    trackOnboardingStep("building", "completed", { contacts_preview: titles.length });
    setTimeout(() => setPhase("confirm"), 800);
  };

  const handleDomainSubmit = async (inputDomain: string) => {
    setDomain(inputDomain);
    trackOnboardingStep("input", "completed", { domain: inputDomain });
    setPhase("building");
    trackOnboardingStep("scrape", "started", { domain: inputDomain });
    setBuildStep(0);
    setBuildProgress(5);

    startBuildAnimation(BUILD_STEPS.length);

    try {
      const trialSnapshot = loadTrialPreviewForDomain(inputDomain);
      if (trialSnapshot) {
        trackOnboardingStep("scrape", "completed", { source: "landing_preview" });
        trackOnboardingStep("analyze", "completed", { source: "landing_preview" });
        completeBuildFromAnalysis(analysisFromTrialPreview(trialSnapshot, inputDomain), inputDomain);
        return;
      }

      // 1. Scrape
      const { data: scrapeData, error: scrapeErr } = await supabase.functions.invoke("onboarding-scrape", {
        body: { url: inputDomain },
      });
      if (scrapeErr || !scrapeData?.success) {
        trackOnboardingStep("scrape", "failed", { error: scrapeErr?.message || scrapeData?.error });
        const fallbackSnapshot = loadTrialPreviewForDomain(inputDomain);
        if (fallbackSnapshot) {
          trackOnboardingStep("analyze", "completed", { source: "landing_preview_after_scrape_failure" });
          completeBuildFromAnalysis(analysisFromTrialPreview(fallbackSnapshot, inputDomain), inputDomain);
          return;
        }
        const fallbackAnalysis = analysisFromTrialPreview({ hostname: normalizeDomain(inputDomain), preview: {} }, inputDomain);
        trackOnboardingStep("analyze", "completed", { source: "domain_fallback" });
        completeBuildFromAnalysis(fallbackAnalysis, inputDomain);
        return;
      }
      trackOnboardingStep("scrape", "completed");

      // 2. Analyze
      trackOnboardingStep("analyze", "started");
      const { data: analysisData, error: analysisErr } = await supabase.functions.invoke("onboarding-analyze", {
        body: {
          markdown: scrapeData.markdown,
          branding: scrapeData.branding,
          metadata: scrapeData.metadata,
          domain: inputDomain,
        },
      });
      if (analysisErr || !analysisData?.success) {
        trackOnboardingStep("analyze", "failed", { error: analysisErr?.message || analysisData?.error });
        throw new Error(analysisData?.error || "Analysis failed");
      }
      trackOnboardingStep("analyze", "completed");

      completeBuildFromAnalysis(analysisData.analysis as AnalysisResult, inputDomain);
    } catch (error: any) {
      if (buildIntervalRef.current) clearInterval(buildIntervalRef.current);
      trackOnboardingStep("building", "failed", { error: error?.message });
      toast({ title: "Setup paused", description: error.message || "We couldn't finish setup. Try again or enter a short description.", variant: "destructive" });
      setPhase("input");
    }
  };

  const handleComplete = async (skipEmail = false, opts: { skipNavigate?: boolean } = {}) => {
    setFinalizing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Find existing auto-created tenant
      const { data: membership } = await supabase
        .from("tenant_memberships")
        .select("tenant_id")
        .eq("user_id", user.id)
        .limit(1)
        .single();

      let tenantId = membership?.tenant_id;

      // Provision via client-management
      const { data: provisionData, error: provisionErr } = await supabase.functions.invoke("client-management", {
        body: {
          email: user.email || connectedEmail,
          domain: domain,
          tier: "free",
          company_name: analysis?.company?.name || domain,
          user_id: user.id,
        },
        headers: { "x-action": "create" },
      });
      if (provisionErr) throw new Error(provisionErr.message || "Provisioning failed");

      const provisionedTenantId = provisionData?.client_id || tenantId;
      tenantId = provisionedTenantId;

      if (!tenantId) throw new Error("No tenant created");

      // Update tenant — persist confirmed vertical if one was chosen in ConfirmSetup
      const tenantUpdate: Record<string, unknown> = {
        name: analysis?.company?.name || domain,
        domain: domain,
        client_email: user.email,
        client_status: "active",
        onboarding_completed: true,
      };
      if (confirmedVerticalSlug) tenantUpdate.target_vertical_slug = confirmedVerticalSlug;
      await supabase.from("tenants").update(tenantUpdate).eq("id", tenantId);

      // Pull tenant's selected vertical so we can seed defaults for sparse fields
      const { data: tenantRow } = await supabase
        .from("tenants")
        .select("target_vertical_slug")
        .eq("id", tenantId)
        .maybeSingle();
      const verticalSlug =
        confirmedVerticalSlug ??
        (tenantRow as { target_vertical_slug?: string | null } | null)?.target_vertical_slug ??
        null;

      let defaults: VerticalIcpDefaults | null = null;
      if (verticalSlug) {
        const { data: seed } = await supabase
          .from("vertical_seeds")
          .select("icp_defaults")
          .eq("slug", verticalSlug)
          .eq("is_active", true)
          .maybeSingle();
        const seedDefaults = (seed?.icp_defaults ?? null) as Partial<VerticalIcpDefaults> | null;
        if (seedDefaults) {
          defaults = {
            titles: Array.isArray(seedDefaults.titles) ? seedDefaults.titles : [],
            industries: Array.isArray(seedDefaults.industries) ? seedDefaults.industries : [],
            company_sizes: Array.isArray(seedDefaults.company_sizes) ? seedDefaults.company_sizes : [],
            regions: Array.isArray(seedDefaults.regions) ? seedDefaults.regions : [],
            pain_points: Array.isArray(seedDefaults.pain_points) ? seedDefaults.pain_points : [],
          };
        } else {
          defaults = verticalIcpDefaults(verticalSlug);
        }
      }

      const mergedTitles = fillFromDefaults(analysis?.icp_signals?.target_titles, defaults?.titles);
      const mergedIndustries = fillFromDefaults(analysis?.icp_signals?.target_industries, defaults?.industries);
      const mergedCompanySizes = fillFromDefaults(analysis?.icp_signals?.target_company_sizes, defaults?.company_sizes);
      const mergedRegions = fillFromDefaults(analysis?.icp_signals?.target_regions, defaults?.regions);
      const mergedPainPoints = fillFromDefaults(analysis?.icp_signals?.pain_points, defaults?.pain_points);

      // Save ICP config
      const senderName = confirmedSenderName || user.user_metadata?.full_name || user.email?.split("@")[0] || "";
      await supabase.from("tenant_settings").update({
        booking_link: null,
        conversion_goal: "book_meeting",
        icp_config: {
          industries: mergedIndustries,
          company_sizes: mergedCompanySizes,
          titles: mergedTitles,
          regions: mergedRegions,
          target_regions: mergedRegions,
          // Seed structured geo targeting (default = anywhere; user can narrow on Train page)
          geo_targeting: {
            mode: mergedRegions.length > 0 ? "regions" : "anywhere",
            regions: mergedRegions,
            local: null,
            exclude: [],
          },
          pain_points: mergedPainPoints,
          ai_analysis: analysis,
          vertical_slug: verticalSlug,
          vertical_seeded_fields: {
            titles: (analysis?.icp_signals?.target_titles?.length ?? 0) === 0 && (defaults?.titles?.length ?? 0) > 0,
            industries: (analysis?.icp_signals?.target_industries?.length ?? 0) === 0 && (defaults?.industries?.length ?? 0) > 0,
            company_sizes: (analysis?.icp_signals?.target_company_sizes?.length ?? 0) === 0 && (defaults?.company_sizes?.length ?? 0) > 0,
            regions: (analysis?.icp_signals?.target_regions?.length ?? 0) === 0 && (defaults?.regions?.length ?? 0) > 0,
            pain_points: (analysis?.icp_signals?.pain_points?.length ?? 0) === 0 && (defaults?.pain_points?.length ?? 0) > 0,
          },
        },
        sending_identity: {
          name: senderName,
          email: confirmedSenderEmail || connectedEmail || user.email,
          signature: null,
        },
      } as any).eq("tenant_id", tenantId);

      // Persist email if connected
      if (!skipEmail && emailMethod && connectedEmail) {
        try {
          if (emailMethod === "oauth" && oauthProvider) {
            await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/oauth-email-connect?action=complete`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json", "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
                body: JSON.stringify({ tenant_id: tenantId, email: connectedEmail, provider: oauthProvider, name: oauthName || senderName }),
              }
            );
          } else if (emailMethod === "gmail" && gmailPassword) {
            await supabase.functions.invoke("connect-email-account", {
              body: {
                tenant_id: tenantId,
                email: connectedEmail,
                provider: "smtp",
                smtp_host: "smtp.gmail.com",
                smtp_port: 587,
                smtp_password: gmailPassword,
                imap_host: "imap.gmail.com",
                imap_port: 993,
                imap_password: gmailPassword,
              },
            });
          }
        } catch (emailErr) {
          console.warn("Email connection persistence failed:", emailErr);
        }
      }

      track("onboarding_complete", { tenantId });
      trackEvent("onboarding_completed", { tenant_id: tenantId });
      trackOnboardingStep("finalize", "completed", { tenant_id: tenantId, skipped_email: skipEmail });
      toast({ title: "Activating your AI SDR…", description: "Discovering prospects and sending your first email." });

      // Fire-and-forget: auto-discover up to 10 verticals from the freshly-saved ICP.
      // Runs in the background so onboarding finishes immediately; user sees verticals on first visit.
      supabase.functions
        .invoke("discover-verticals", {
          body: {
            tenant_id: tenantId,
            max: 10,
            // If preview already finished, insert immediately (no second AI call).
            // Otherwise the function falls back to running discovery itself.
            verticals: verticalPreviewRef.current ?? undefined,
          },
        })
        .catch((e) => console.warn("vertical discovery (non-blocking):", e));

      // SYNC activation chain: discovery → cadence → first send.
      // Awaited so we know if first email actually shipped before redirecting.
      try {
        const { data: activationResult, error: activationErr } = await supabase.functions.invoke(
          "finalize-activation",
          { body: { tenant_id: tenantId } }
        );
        if (activationErr) {
          console.warn("finalize-activation error:", activationErr);
        } else if (activationResult?.first_email) {
          toast({
            title: "First email sent ✓",
            description: `To ${activationResult.first_email.to}`,
          });
        } else {
          toast({
            title: "Setup complete",
            description: "Discovery is running — your first email will go out shortly.",
          });
        }
      } catch (actErr) {
        console.warn("Activation chain failed (will self-heal):", actErr);
      }

      clearPersistedOnboarding();
      if (!opts.skipNavigate) window.location.reload();
    } catch (error: any) {
      trackOnboardingStep("finalize", "failed", { error: error?.message });
      toast({ title: "Setup failed", description: error.message, variant: "destructive" });
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {phase === "input" && (
        <motion.div key="input" exit={{ opacity: 0 }}>
          <InputPhase onSubmit={handleDomainSubmit} loading={false} />
        </motion.div>
      )}
      {phase === "building" && (
        <motion.div key="building" exit={{ opacity: 0 }}>
          <BuildingPhase currentStep={buildStep} progress={buildProgress} />
        </motion.div>
      )}
      {phase === "confirm" && analysis && (
        <motion.div key="confirm" exit={{ opacity: 0 }}>
          <ConfirmSetupPhase
            analysis={analysis}
            domain={domain}
            initialSenderName={confirmedSenderName || authUserDefaults.name}
            initialSenderEmail={confirmedSenderEmail || authUserDefaults.email}
            onBack={() => setPhase("building")}
            onSubmit={({ analysis: nextAnalysis, verticalSlug, senderName, senderEmail }) => {
              setAnalysis(nextAnalysis);
              setConfirmedVerticalSlug(verticalSlug);
              setConfirmedSenderName(senderName);
              setConfirmedSenderEmail(senderEmail);
              // Refresh the derived preview lines with any edited titles / value prop
              const titles = nextAnalysis.icp_signals?.target_titles || [];
              setEmailPreviews(
                (nextAnalysis.messaging_angles || []).slice(0, 3).map((m, i) => ({
                  to: `${titles[i] || "Decision Maker"} at Target Company`,
                  subject: m.hook || m.angle,
                  preview: m.value_prop || nextAnalysis.company?.value_proposition || "",
                }))
              );
              setPhase("activation");
            }}
          />
        </motion.div>
      )}
      {phase === "activation" && analysis && (
        <motion.div key="activation" exit={{ opacity: 0 }}>
          <ActivationPhase
            analysis={analysis}
            contactsFound={contactsFound}
            emailPreviews={emailPreviews}
            onContinue={() => setPhase(isAnonymousUser ? "save_account" : "connect_email")}
          />
        </motion.div>
      )}
      {phase === "save_account" && (
        <motion.div key="save_account" exit={{ opacity: 0 }}>
          <SaveAccountPhase
            initialName={confirmedSenderName || authUserDefaults.name}
            initialEmail={confirmedSenderEmail || authUserDefaults.email}
            onSaved={({ email, fullName }) => {
              setAuthUserDefaults({ name: fullName, email });
              setIsAnonymousUser(false);
              if (!confirmedSenderName) setConfirmedSenderName(fullName);
              if (!confirmedSenderEmail) setConfirmedSenderEmail(email);
              setPhase("connect_email");
            }}
          />
        </motion.div>
      )}
      {phase === "connect_email" && (
        <motion.div key="connect" exit={{ opacity: 0 }}>
          <ConnectEmailPhase
            loading={finalizing}
            onConnected={(email, method, password, provider, name) => {
              setConnectedEmail(email);
              setEmailMethod(method);
              if (password) setGmailPassword(password);
              if (provider) setOauthProvider(provider);
              if (name) setOauthName(name);
              // Move to calendar step (skippable). Persistence happens on finalize.
              setPhase("connect_calendar");
            }}
            onSkip={() => setPhase("connect_calendar")}
          />
        </motion.div>
      )}
      {phase === "connect_calendar" && (
        <motion.div key="calendar" exit={{ opacity: 0 }}>
          <ConnectCalendarPhase
            loading={finalizing}
            hasEmailConnected={!!emailMethod && !!connectedEmail}
            onConnect={async (provider) => {
              try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) throw new Error("Not authenticated");
                // Find tenant
                const { data: m } = await supabase
                  .from("tenant_memberships")
                  .select("tenant_id")
                  .eq("user_id", user.id)
                  .limit(1)
                  .single();
                // Persist onboarding decisions BEFORE redirect so we don't lose state
                await handleComplete(!emailMethod, { skipNavigate: true });
                const tenantId = m?.tenant_id;
                const { data, error } = await supabase.functions.invoke("calendar-oauth-start", {
                  body: { provider, tenant_id: tenantId, redirect_after: "/" },
                });
                if (error || !data?.authorize_url) throw new Error(data?.error || "Could not start calendar connection");
                window.location.href = data.authorize_url;
              } catch (e: any) {
                toast({ title: "Calendar connection failed", description: e.message, variant: "destructive" });
              }
            }}
            onSkip={() => handleComplete(!emailMethod)}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function EmailVerificationGate({ email, onResend, resending }: { email: string; onResend: () => void; resending: boolean }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full rounded-xl border border-border bg-card p-8 text-center space-y-5">
        <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
          <Mail className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-foreground">Verify your email to continue</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We sent a verification link to <span className="text-foreground font-medium">{email}</span>.
            Click it to activate your AutoPitch workspace.
          </p>
        </div>
        <Button onClick={onResend} disabled={resending} className="w-full h-10">
          {resending ? "Sending…" : "Resend verification email"}
        </Button>
        <p className="text-[11px] text-muted-foreground">
          Already verified? Refresh this page.
        </p>
      </div>
    </div>
  );
}

export default function Onboarding() {
  const [checking, setChecking] = useState(true);
  const [needsVerify, setNeedsVerify] = useState<{ email: string } | null>(null);
  const [resending, setResending] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!active) return;
      // Email signups: enforce verification before onboarding.
      // Google/Microsoft already pre-verify the email at the provider.
      // Anonymous trial users have no email — skip the gate entirely; they
      // capture an email in the save_account phase later.
      const provider = (user?.app_metadata?.provider as string | undefined) ?? "email";
      const isAnonymous = Boolean((user as any)?.is_anonymous);
      const isEmail = provider === "email";
      if (user && !isAnonymous && isEmail && user.email && !user.email_confirmed_at) {
        setNeedsVerify({ email: user.email });
      } else {
        setNeedsVerify(null);
      }
      setChecking(false);
    })();
    return () => { active = false; };
  }, []);

  const handleResend = async () => {
    if (!needsVerify?.email) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: needsVerify.email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      toast({ title: "Email sent", description: "Check your inbox for the verification link." });
    } catch (e: any) {
      toast({ title: "Couldn't resend", description: e?.message ?? "Try again in a moment.", variant: "destructive" });
    } finally {
      setResending(false);
    }
  };

  if (checking) return null;
  if (needsVerify) {
    return <EmailVerificationGate email={needsVerify.email} onResend={handleResend} resending={resending} />;
  }
  return <OnboardingFlow />;
}
